import { Link } from "react-router-dom";
import { QrCode, BarChart3, Zap, RefreshCw, Globe2, Shield, ArrowRight, LineChart } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";

const features = [
  {
    icon: RefreshCw,
    title: "Dynamic destinations",
    desc: "Change the URL behind any QR at any moment. The printed code never changes.",
    accent: "from-blue-500/10 to-blue-500/0 border-blue-500/20 text-blue-600",
  },
  {
    icon: BarChart3,
    title: "Scan analytics",
    desc: "See total scans, weekly trends, device breakdown, and approximate location.",
    accent: "from-cyan-500/10 to-cyan-500/0 border-cyan-500/20 text-cyan-600",
  },
  {
    icon: Zap,
    title: "Instant setup",
    desc: "Name a campaign, drop a URL, download a print-ready QR image. Under 10 seconds.",
    accent: "from-violet-500/10 to-violet-500/0 border-violet-500/20 text-violet-600",
  },
  {
    icon: Globe2,
    title: "Geo insights",
    desc: "IP-based city and country signals for every scan, plus device type detection.",
    accent: "from-emerald-500/10 to-emerald-500/0 border-emerald-500/20 text-emerald-600",
  },
  {
    icon: Shield,
    title: "Owned & isolated",
    desc: "Each business only sees its own campaigns. Bank-grade JWT authentication.",
    accent: "from-amber-500/10 to-amber-500/0 border-amber-500/20 text-amber-600",
  },
  {
    icon: LineChart,
    title: "Exportable data",
    desc: "Pull the entire scan history as CSV. Feed it into your BI tool of choice.",
    accent: "from-pink-500/10 to-pink-500/0 border-pink-500/20 text-pink-600",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <span
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-[0.15em] border border-blue-100"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                Dynamic QR platform
              </span>
              <h1
                data-testid="hero-title"
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-slate-900"
              >
                One QR code.
                <br />
                <span className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  Infinite destinations.
                </span>
              </h1>
              <p
                data-testid="hero-subtitle"
                className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
              >
                Print once. Redirect anywhere. Qdurator gives your business
                dynamic QR campaigns with real-time scan analytics, device
                detection, and approximate geolocation.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-6 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all"
                    data-testid="hero-get-started-btn"
                  >
                    Start free
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 px-6 rounded-xl font-semibold border-slate-300"
                    data-testid="hero-login-btn"
                  >
                    I have an account
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  Unlimited campaigns
                </div>
              </div>
            </div>

            {/* Hero visual */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-blue-500/20 via-cyan-400/10 to-transparent rounded-3xl blur-2xl" />
                <div className="relative bg-white border border-slate-200 rounded-3xl p-8 shadow-xl animate-float-slow">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
                        Campaign
                      </div>
                      <div className="font-bold text-slate-900 text-lg mt-1">
                        Autumn 2026 Flyer
                      </div>
                    </div>
                    <div className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      LIVE
                    </div>
                  </div>
                  <div className="aspect-square rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center p-6">
                    <div className="w-full h-full bg-white rounded-xl p-4 grid grid-cols-8 gap-0.5 shadow-inner">
                      {Array.from({ length: 64 }).map((_, i) => (
                        <div
                          key={i}
                          className={
                            (i * 7) % 3 === 0 || i % 5 === 0 || i === 0 || i === 7 || i === 56
                              ? "bg-slate-900 rounded-[2px]"
                              : "bg-transparent"
                          }
                        />
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Short link</span>
                      <span className="font-mono font-semibold text-slate-900">
                        qdurator/aX92kZ
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Total scans</span>
                      <span className="font-mono font-bold text-blue-600 tabular-nums">
                        4,281
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600">
            Everything you need
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Print-and-forget QR codes,
            <br />
            <span className="text-slate-500">with a control panel behind them.</span>
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-card-${f.title.replace(/\s+/g, '-').toLowerCase()}`}
              className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-500/30 transition-all duration-200"
            >
              <div
                className={`w-11 h-11 rounded-xl border flex items-center justify-center bg-gradient-to-br ${f.accent}`}
              >
                <f.icon className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <h3 className="mt-5 font-bold text-slate-900 text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-10 sm:p-14 border border-slate-800">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
              Ready to make your QR codes smarter?
            </h2>
            <p className="mt-4 text-slate-300 text-lg">
              Set up your first campaign in under a minute. Track every scan.
              Update destinations without reprinting a thing.
            </p>
            <div className="mt-8">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-white hover:bg-slate-100 text-slate-900 h-12 px-6 rounded-xl font-semibold"
                  data-testid="cta-signup-btn"
                >
                  Create your account
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <QrCode className="w-4 h-4 text-white" />
            </div>
            <span className="font-black tracking-tight text-slate-900">Qdurator</span>
          </div>
          <div className="text-sm text-slate-500">
            © {new Date().getFullYear()} Qdurator. Built for modern businesses.
          </div>
        </div>
      </footer>
    </div>
  );
}
